'use client';

import { Toaster as ChakraToaster, Portal, Spinner, Stack, Toast, createToaster } from '@chakra-ui/react';
import { OVERLAY_CORNER_WIDTH_PX, TOAST_EDGE_INSET_PX, TOAST_INSET_VARS } from '@/utils/toastInsets';

const EDGE_INSET = `${TOAST_EDGE_INSET_PX}px`;
const OVERLAY_CORNER_WIDTH = `${OVERLAY_CORNER_WIDTH_PX}px`;

export const toaster = createToaster({
	placement: 'bottom-end',
	pauseOnPageIdle: true,
	// Both corner insets are late-bound so an overlay sharing the corner can move toasts clear of
	// it — sideways past a drawer, or up on top of the upload queue. See utils/toastInsets.
	offsets: {
		top: EDGE_INSET,
		left: EDGE_INSET,
		right: `var(${TOAST_INSET_VARS.right}, ${EDGE_INSET})`,
		bottom: `var(${TOAST_INSET_VARS.bottom}, ${EDGE_INSET})`,
	},
});

type ToastRenderProps = Parameters<Parameters<typeof ChakraToaster>[0]['children']>[0];

const renderToast = (toast: ToastRenderProps) => (
	<Toast.Root width={{ md: OVERLAY_CORNER_WIDTH }} borderRadius="2xl">
		{toast.type === 'loading' ? <Spinner size="sm" color="brand.fg" /> : <Toast.Indicator />}
		<Stack gap="1" flex="1" maxWidth="100%">
			{toast.title && <Toast.Title>{toast.title}</Toast.Title>}
			{toast.description && (
				<Toast.Description
					whiteSpace="pre-line"
					overflowWrap="anywhere"
					maxH="min(40vh, 20rem)"
					overflowY="auto"
					pr={1}
				>
					{toast.description}
				</Toast.Description>
			)}
		</Stack>
		{toast.action && <Toast.ActionTrigger>{toast.action.label}</Toast.ActionTrigger>}
		{toast.closable && <Toast.CloseTrigger />}
	</Toast.Root>
);

export const Toaster = () => {
	return (
		<Portal>
			<ChakraToaster toaster={toaster} insetInline={{ mdDown: '4' }}>
				{renderToast}
			</ChakraToaster>
		</Portal>
	);
};

// Inline variant without its own Portal, for inclusion inside a global overlays tray
export const ToasterInline = () => {
	return (
		<ChakraToaster toaster={toaster} insetInline={{ mdDown: '0' }}>
			{renderToast}
		</ChakraToaster>
	);
};
